import { axialHexDistance } from '../../hexmap/coords';
import type { HexTileMapKey } from '../../hexmap/model/types';
import { pathTo, reachableFrom } from '../../hexmap/pathfinding';
import { tickDerived } from '../derived';
import {
  addHeading,
  forget,
  nearestFree,
  type Occupancy,
  occupancyOf,
  SETTLE_RADIUS,
  unitsByMint,
} from '../occupancy';
import { redirect } from '../redirect';
import { mintOrder } from '../territory';
import type { Cell, GroupId, PlayerId, SimContext, SimState, SystemFn, Unit } from '../types';
import { canHeal } from './heal';

/**
 * Unit groups: members share `Unit.groupId`, and `SimState.groups` names each
 * group's leader, the member last ordered to move. The other members
 * (followers) keep to the nearest free tiles around it: every FOLLOW_TICKS a
 * follower that has fallen out of formation re-targets. One close to the
 * leader heads for the tiles around the leader's destination (so it travels
 * alongside); one further off (behind, or run ahead) closes on the tiles
 * around where the leader is now. Followers on an attack or heal order,
 * automated or inside ones sit this out. While the leader is on an attack
 * order, healers trail it instead of closing on the fight: one within
 * TRAIL_RADIUS of the leader stays where it is (tend.ts sends it to the
 * wounded), one further off closes to tiles 2 or more from the leader. Only rival units and buildings the owner can see
 * count when picking tiles (fog-safe). A group with one member left dissolves; a lost
 * leader is replaced by the newest member. Runs after pursuit, before
 * movement.
 */

const FOLLOW_TICKS = 5;
/** How close (hexes) a healer keeps to a group leader that's fighting. */
const TRAIL_RADIUS = 3;

/** Smallest hex radius whose disc holds `n` units (1 → 0, 2..7 → 1, 8..19 → 2). */
function formationRadius(n: number): number {
  let r = 0;
  while (1 + 3 * r * (r + 1) < n) r++;
  return r;
}

function destination(u: Unit): Cell {
  return u.path && u.path.length > 0 ? u.path[u.path.length - 1] : u.pos;
}

/** Mints a new group id from the shared entity counter. */
function mintGroupId(state: SimState): GroupId {
  const id = `g${state.nextEntityId}`;
  state.nextEntityId += 1;
  return id;
}

/** The lowest group number `owner` isn't using. */
function freeGroupNumber(state: SimState, owner: PlayerId): number {
  const used = new Set<number>();
  for (const u of Object.values(state.units)) {
    if (u.owner !== owner || u.groupId === undefined) continue;
    const n = state.groups?.[u.groupId]?.number;
    if (n !== undefined) used.add(n);
  }
  let n = 1;
  while (used.has(n)) n++;
  return n;
}

/**
 * Makes `units` (own, at least two) a new group led by `leader`, numbered
 * with the owner's lowest free group number (kept while the group lasts, so
 * other groups' numbers never shift).
 */
function formGroup(state: SimState, units: readonly Unit[], leader: Unit): GroupId {
  // Counted before the units leave their old groups, which may live on.
  const number = freeGroupNumber(state, leader.owner);
  const id = mintGroupId(state);
  state.groups = { ...state.groups, [id]: { leader: leader.id, number } };
  for (const u of units) u.groupId = id;
  return id;
}

/**
 * Drops group ids nobody holds, dissolves groups down to one member, and
 * replaces a missing leader with the newest member.
 */
function maintainGroups(state: SimState): void {
  const members = new Map<GroupId, Unit[]>();
  for (const u of unitsByMint(state)) {
    if (u.groupId === undefined) continue;
    if (!state.groups?.[u.groupId]) {
      delete u.groupId;
      continue;
    }
    const list = members.get(u.groupId);
    if (list) list.push(u);
    else members.set(u.groupId, [u]);
  }
  if (!state.groups) return;
  for (const [id, group] of Object.entries(state.groups)) {
    const list = members.get(id) ?? [];
    if (list.length <= 1) {
      for (const u of list) delete u.groupId;
      delete state.groups[id];
      continue;
    }
    if (!list.some((u) => u.id === group.leader)) group.leader = list[list.length - 1].id;
  }
  if (Object.keys(state.groups).length === 0) delete state.groups;
}

/** Group id → its members, oldest first. */
function groupMembers(state: SimState): Map<GroupId, Unit[]> {
  const members = new Map<GroupId, Unit[]>();
  for (const u of unitsByMint(state)) {
    if (u.groupId === undefined) continue;
    const list = members.get(u.groupId);
    if (list) list.push(u);
    else members.set(u.groupId, [u]);
  }
  return members;
}

function follow(
  state: SimState,
  ctx: SimContext,
  occ: Occupancy,
  blocked: ReadonlySet<HexTileMapKey>,
  leader: Unit,
  followers: readonly Unit[],
  size: number
): void {
  const spread = formationRadius(size);
  const leaderDest = destination(leader);
  const fighting = leader.order?.kind === 'attack';
  const trails = (f: Unit) => fighting && canHeal(ctx.rules, f);
  const misplaced: Unit[] = [];
  for (const f of followers) {
    const dest = destination(f);
    const inPlace = trails(f)
      ? axialHexDistance(dest, leader.pos) <= TRAIL_RADIUS
      : axialHexDistance(dest, leaderDest) <= spread &&
        axialHexDistance(f.pos, leader.pos) <= spread + 1;
    if (!inPlace) misplaced.push(f);
  }
  for (const f of misplaced) {
    const trail = trails(f);
    const near = !trail && axialHexDistance(f.pos, leader.pos) <= spread + 1;
    const anchor = near ? leaderDest : leader.pos;
    const terrain = ctx.rules.units[f.type]?.terrain ?? [];
    forget(occ, f);
    const reach = reachableFrom(ctx.terrain, f.pos, terrain, blocked);
    const who = { id: f.id, owner: f.owner, type: f.type };
    const min = trail ? 2 : 1;
    const spot = nearestFree(ctx, occ, who, anchor, min, SETTLE_RADIUS, reach, undefined, ['open']);
    if (!spot) continue;
    redirect(state, f, pathTo(reach, spot));
    // A follower's attackMove stays (it still engages) but its target is stale
    // once formation steers it; movement drops the order on arrival.
    if (f.order?.kind === 'move') delete f.order;
    addHeading(occ, f, spot);
  }
}

const formation: SystemFn = (state, ctx) => {
  maintainGroups(state);
  if (!state.groups || state.tick % FOLLOW_TICKS !== 0) return;
  // One view per player: followers only avoid what their owner can see.
  const derived = tickDerived(state, ctx);
  const views = new Map<PlayerId, { occ: Occupancy; blocked: ReadonlySet<HexTileMapKey> }>();
  const viewOf = (player: PlayerId) => {
    let view = views.get(player);
    if (!view) {
      const vision = derived.vision(player);
      view = {
        occ: occupancyOf(state, ctx.rules, { player, vision }),
        blocked: derived.blockers(player),
      };
      views.set(player, view);
    }
    return view;
  };
  const members = groupMembers(state);
  const ids = Object.keys(state.groups).sort((a, b) => mintOrder(a) - mintOrder(b));
  for (const id of ids) {
    const leader = state.units[state.groups[id]?.leader ?? ''];
    if (!leader || leader.inside) continue;
    const list = members.get(id) ?? [];
    const followers = list.filter(
      (u) =>
        u.id !== leader.id &&
        !u.inside &&
        !u.auto &&
        u.order?.kind !== 'attack' &&
        u.order?.kind !== 'heal'
    );
    if (followers.length === 0) continue;
    const { occ, blocked } = viewOf(leader.owner);
    follow(state, ctx, occ, blocked, leader, followers, list.length);
  }
};

export { formation, formGroup, maintainGroups };
